/**
 * The registry's enums, and how they render.
 *
 * The numeric values mirror `IAsetquUtilityRegistry` exactly. They are an
 * on-chain ABI: reordering them here silently mislabels every record, and
 * nothing would fail loudly.
 */

export const REGISTRY_STATUS = [
  "Unregistered",
  "Pending",
  "Registered",
  "Active",
  "Deprecated",
  "Suspended",
  "Disabled",
  "Revoked",
  "Expired",
] as const;

export type RegistryStatus = (typeof REGISTRY_STATUS)[number];

export const REGISTRY_TYPE = [
  "Undefined",
  "InternalUtility",
  "ExternalVendor",
  "ExternalUtility",
] as const;

export type RegistryType = (typeof REGISTRY_TYPE)[number];

export const UTILITY_TYPE = [
  "Undefined",
  "FinalConsumption",
  "TemporaryLock",
  "Settlement",
] as const;

export type UtilityType = (typeof UTILITY_TYPE)[number];

export function statusName(value: number): RegistryStatus {
  return REGISTRY_STATUS[value] ?? "Unregistered";
}

export function registryTypeName(value: number): RegistryType {
  return REGISTRY_TYPE[value] ?? "Undefined";
}

export function utilityTypeName(value: number): UtilityType {
  return UTILITY_TYPE[value] ?? "Undefined";
}

export type StatusTone = "good" | "warn" | "bad" | "neutral";

export type StatusPresentation = {
  label: string;
  tone: StatusTone;
  /** Whether a user should be encouraged to transact with this record. */
  transactable: boolean;
  explanation: string;
};

/**
 * Status → what the page says.
 *
 * `Unregistered` is the most important row and the easiest to get wrong. It is
 * the answer to "someone showed me this address, is it official?", so it must
 * read as a clear negative — never as a 404, which a visitor will mistake for a
 * loading error and try again.
 */
export const STATUS_PRESENTATION: Record<RegistryStatus, StatusPresentation> = {
  Unregistered: {
    label: "Tidak terdaftar",
    tone: "bad",
    transactable: false,
    explanation:
      "Tidak ada catatan untuk ID ini di UtilityRegistry. Ini bukan bagian resmi dari ekosistem ASETQU.",
  },
  Pending: {
    label: "Menunggu verifikasi",
    tone: "neutral",
    transactable: false,
    explanation:
      "Pendaftaran sudah tercatat tetapi belum disetujui governance. Belum boleh dianggap terverifikasi.",
  },
  Registered: {
    label: "Terdaftar, belum aktif",
    tone: "neutral",
    transactable: false,
    explanation:
      "Tercatat dan lolos pemeriksaan identitas, tetapi masih dalam masa tunggu aktivasi. Belum melayani transaksi.",
  },
  Active: {
    label: "Aktif",
    tone: "good",
    transactable: true,
    explanation: "Terverifikasi dan aktif. Ini versi yang harus dipakai untuk transaksi baru.",
  },
  Deprecated: {
    label: "Versi lama",
    tone: "warn",
    transactable: false,
    // The nuance that matters: deprecated is not broken. Existing positions
    // must still be settleable, and telling users otherwise causes panic.
    explanation:
      "Sudah digantikan versi yang lebih baru. Tidak menerima transaksi baru, tetapi posisi yang sudah ada tetap dapat diselesaikan dan ditarik.",
  },
  Suspended: {
    label: "Ditangguhkan",
    tone: "warn",
    transactable: false,
    explanation:
      "Sedang dalam investigasi. Tidak menerima aktivitas baru. Penarikan dana yang sudah ada tetap harus dapat dilakukan.",
  },
  Disabled: {
    label: "Dinonaktifkan",
    tone: "bad",
    transactable: false,
    explanation:
      "Ditarik setelah insiden, bug, atau audit yang gagal. Jangan lakukan transaksi baru. Jalur refund dan penarikan tetap terbuka.",
  },
  Revoked: {
    label: "Dicabut",
    tone: "bad",
    transactable: false,
    explanation:
      "Status resminya dicabut permanen. Sertifikat yang mungkin masih dipegang tidak lagi berlaku.",
  },
  Expired: {
    label: "Masa berlaku habis",
    tone: "warn",
    transactable: false,
    explanation:
      "Masa berlakunya lewat dan belum diperpanjang. Tidak terverifikasi sampai diperpanjang governance.",
  },
};
